import type { FunctionComponent } from 'react';

import { useSearchParams } from 'react-router';
import { Alert, Tabs } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { breakpoints } from 'styles/theme.variables';

import { TAB_VALUES } from 'constants/menuManagement';

import MenuInfo from 'components/MenuManagement/MenuEdit/MenuInfo';
import { MenuItems } from 'components/MenuManagement/MenuEdit/MenuItems';
import MenuMapping from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';
import { MenuPhotos } from 'components/MenuManagement/MenuEdit/MenuPhotos';
import { BellIconMd } from '@chownow/pantry/icons';

type MenuTabsProps = {
  allowMapping: boolean;
  menu?: {
    companyId: string;
    name: string;
    associatedMerchants: string[];
    clonedMenu: boolean;
  };
};

const MOBILE_ALERT_STYLES = {
  root: { alignItems: 'center' },
  icon: { alignSelf: 'center', width: 28, height: 28 },
  body: { alignSelf: 'center' },
};

// Single source of truth for the active tab: derived fresh from the URL on every
// render (not mirrored into local state), so it can never drift from `?tab=`.
function getActiveTab(searchParams: URLSearchParams, allowMapping: boolean) {
  const requestedTab = searchParams.get('tab');
  if (requestedTab === TAB_VALUES.MENU_PHOTOS) return TAB_VALUES.MENU_PHOTOS;
  if (requestedTab === TAB_VALUES.MENU_MAPPING && allowMapping) return TAB_VALUES.MENU_MAPPING;
  if (requestedTab === TAB_VALUES.MENU_INFO) return TAB_VALUES.MENU_INFO;
  return TAB_VALUES.MENU_ITEMS;
}

export const MenuTabs: FunctionComponent<MenuTabsProps> = ({ allowMapping, menu }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getActiveTab(searchParams, allowMapping);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });

  // `replace` so each tab switch updates the URL without piling up browser history entries.
  const handleTabChange = (value: string | null) => {
    const nextTab = value || TAB_VALUES.MENU_ITEMS;
    setSearchParams(
      prev => {
        prev.set('tab', nextTab);
        return prev;
      },
      { replace: true },
    );
  };

  if (!menu) {
    return <MenuInfo />;
  }

  return (
    <Tabs value={activeTab} onChange={handleTabChange}>
      <Tabs.List mb="xl" data-size={isMobile ? 'sm' : 'lg'}>
        <Tabs.Tab value={TAB_VALUES.MENU_ITEMS}>Items</Tabs.Tab>
        <Tabs.Tab value={TAB_VALUES.MENU_PHOTOS}>Photos</Tabs.Tab>
        <Tabs.Tab value={TAB_VALUES.MENU_INFO}>Details</Tabs.Tab>
        {allowMapping && <Tabs.Tab value={TAB_VALUES.MENU_MAPPING}>Mapping</Tabs.Tab>}
      </Tabs.List>
      <Tabs.Panel value={TAB_VALUES.MENU_ITEMS}>
        <MenuItems onNavigateToPhotos={() => handleTabChange(TAB_VALUES.MENU_PHOTOS)} />
      </Tabs.Panel>
      <Tabs.Panel value={TAB_VALUES.MENU_PHOTOS}>
        {/* Skips MenuPhotos until active — Tabs.Panel keeps inactive tabs mounted. */}
        {activeTab === TAB_VALUES.MENU_PHOTOS && <MenuPhotos />}
      </Tabs.Panel>
      <Tabs.Panel value={TAB_VALUES.MENU_INFO}>
        <MenuInfo />
      </Tabs.Panel>
      {allowMapping && (
        <Tabs.Panel value={TAB_VALUES.MENU_MAPPING}>
          {isMobile ? (
            <Alert icon={<BellIconMd />} variant="blue" styles={MOBILE_ALERT_STYLES}>
              Please create and edit mapping on the desktop experience.
            </Alert>
          ) : (
            activeTab === TAB_VALUES.MENU_MAPPING && <MenuMapping />
          )}
        </Tabs.Panel>
      )}
    </Tabs>
  );
};
